import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    createDateFormatter,
    type DataTableColumn,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getServiceNotificationMailerTrackings } from '@/modules/Service/services'
import { ServiceMailerType } from '@/modules/Service/types'
import { Mail } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface ServiceNotificationMailerTrackingProps {
    serviceID: number
}

const ServiceNotificationMailerTracking: React.FC<
    ServiceNotificationMailerTrackingProps
> = ({ serviceID }) => {
    const { t } = useTranslation()

    const {
        data: trackings = [],
        renderState,
        isFetching,
    } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.SERVICES,
                serviceID,
                'notifications',
                'trackings',
            ],
            queryFn: async () => {
                const res = await getServiceNotificationMailerTrackings({
                    serviceID,
                })
                return (res?.results || res?.result || res?.data) ?? []
            },
        },
        {
            showLoading: true,
            showError: true,
        }
    )

    const columns = useMemo(
        (): DataTableColumn<ServiceMailerType>[] => [
            {
                key: 'task_jid',
                title: 'Task JID',
                width: 300,
            },
            {
                key: 'metadata' as any,
                title: t('subject'),
                width: 300,
                valueGetter: (row) => row.metadata?.subject || '-',
            },
            {
                key: 'task_name' as any,
                title: t('notification_name'),
                width: 200,
                valueGetter: (row) =>
                    row.metadata?.metadata?.mailer?.customer_mailer_name || '-',
            },
            {
                key: 'task_bind' as any,
                title: t('receiver'),
                width: 200,
                valueGetter: (row) =>
                    row.metadata?.metadata?.mailer?.mailer_receiver || '-',
            },
            {
                key: 'task_scope' as any,
                title: 'CC',
                width: 200,
                valueGetter: (row) =>
                    row.metadata?.metadata?.mailer?.mailer_cc || '-',
            },
            {
                key: 'task_description' as any,
                title: t('sender'),
                width: 300,
                valueGetter: (row) =>
                    row.metadata?.metadata?.sender?.employee_email || '-',
            },
            {
                key: 'task_created_at',
                title: t('created_at'),
                width: 150,
                formatter: createDateFormatter('HH:mm DD/MM/YYYY'),
            },
            {
                key: 'task_status',
                title: t('status'),
                width: 150,
            },
            {
                key: 'task_completed_at',
                title: t('completed_at'),
                width: 150,
                formatter: createDateFormatter('HH:mm DD/MM/YYYY'),
            },
        ],
        [t]
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <Paper className="mt-2">
            <p className="flex items-center gap-2 mb-3 font-medium text-primary">
                <Mail className="h-4 w-4" />
                {t('tracking.mailers')}
            </p>
            <DataTableCustomized<ServiceMailerType>
                columns={columns}
                rows={trackings}
                rowKey={(row) => row.task_uuid}
                isFetching={isFetching}
                emptyMessage={t('no-data')}
                bordered
                size="small"
                pageSize={5}
            />
        </Paper>
    )
}

export default ServiceNotificationMailerTracking
